import { HeaderRootProps } from "@/components/layout/Header";
import { LangKey } from "@vixi/localization";
import { NavigateOptions } from "@vixi/router";
import { ReactNode } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export const expandedSidebarWidth = 189;
export const compactSidebarWidth = 75;

/**
 * @description The state of the confirmation dialog.
 * @param show - Whether the dialog is shown.
 * @param title - The title of the dialog.
 * @param description - The description of the dialog.
 * @param onConfirm - The function to call when the user confirms the dialog - returns true if the dialog should be closed.
 * @param onCancel - The function to call when the user cancels the dialog - returns true if the dialog should be closed.
 */
export interface ConfirmDialogState {
  show: boolean;
  title: LangKey | null;
  description: ReactNode | null;
  confirmText?: LangKey;
  cancelText?: LangKey;
  dialogActions?: ReactNode;
  onConfirm?: () => boolean | Promise<boolean> | void | Promise<void>;
  onCancel?: () => boolean | Promise<boolean> | void | Promise<void>;
}

const default_confirm_dialog: ConfirmDialogState = {
  show: false,
  title: null,
  description: null,
  onConfirm: undefined,
  onCancel: undefined,
};

export interface UiState {
  header: {
    goBack: (() => void) | NavigateOptions | true | JSX.Element | null;
    hasStepper: boolean;
    children?: React.ReactNode;
    isVisible: () => boolean;
    sidebarMargin?: number;
    rootProps?: Omit<HeaderRootProps, "isVisible" | "sidebarWidth">;
  };
  sidebar: {
    mode: "compact" | "expanded";
    disabled: boolean;
    width: number;
    inTransition: boolean;
  };
  courseEditor: {
    mode: "view" | "edit";
  };
  confirmDialog: ConfirmDialogState;
  watchedCourseTour: boolean;
}

export interface UiActions {
  header: {
    setGoBack: (goBack: UiState["header"]["goBack"]) => void;
    setChildren: (children: ReactNode) => void;
    setHeaderProps: (props: UiState["header"]["rootProps"]) => void;
  };
  sidebar: {
    setSidebarMode: (mode: UiState["sidebar"]["mode"]) => void;
    transitionEnded: () => void;
    setDisabled: (disabled: (prev: boolean) => boolean) => void;
  };
  courseEditor: {
    setMode: (mode: UiState["courseEditor"]["mode"]) => void;
  };
  showConfirmDialog: (props: Omit<ConfirmDialogState, "show">) => void;
  hideConfirmDialog: () => void;
  setWatchedCourseTour: (watched: boolean) => void;
}

export const useUiStore = create<UiState & UiActions>()(
  persist(
    (set, get) => ({
      header: {
        goBack: null,
        hasStepper: false,

        rootProps: {},

        setHeaderProps: (props: UiState["header"]["rootProps"]) =>
          set((state) => ({
            ...state,
            header: {
              ...state.header,
              rootProps: {
                ...state.header.rootProps,
                ...props,
              },
            },
          })),

        isVisible: () => {
          return !!get().header.goBack || !!get().header.children;
        },

        setChildren: (children: ReactNode) =>
          set((state) => ({
            ...state,
            header: {
              ...state.header,
              children: children,
            },
          })),

        setGoBack: (goBack: UiState["header"]["goBack"]) =>
          set((state) => ({
            ...state,
            header: {
              ...state.header,
              goBack: goBack,
            },
          })),
      },

      sidebar: {
        mode: "expanded",
        width: expandedSidebarWidth,
        inTransition: false,
        disabled: false,

        setDisabled: (disabled: (prev: boolean) => boolean) =>
          set((state) => ({
            ...state,
            sidebar: {
              ...state.sidebar,
              disabled: disabled(state.sidebar.disabled),
            },
          })),

        setSidebarMode: (mode: UiState["sidebar"]["mode"]) =>
          set((state) => ({
            ...state,
            sidebar: {
              ...state.sidebar,
              mode: mode,
              width: mode === "compact" ? compactSidebarWidth : expandedSidebarWidth,
            },
          })),

        transitionEnded: () => {
          set((state) => ({
            ...state,
            sidebar: {
              ...state.sidebar,
              inTransition: false,
            },
          }));
        },
      },

      courseEditor: {
        mode: "edit",
        setMode: (mode: UiState["courseEditor"]["mode"]) => {
          set((state) => ({
            ...state,
            courseEditor: {
              ...state.courseEditor,
              mode: mode,
            },
          }));
        },
      },

      confirmDialog: default_confirm_dialog,

      showConfirmDialog: (props) =>
        set((state) => ({
          ...state,
          confirmDialog: {
            ...default_confirm_dialog,
            show: true,
            ...props,
          },
        })),

      hideConfirmDialog: () =>
        set((state) => ({
          ...state,
          confirmDialog: {
            ...state.confirmDialog,
            show: false,
            dialogActions: undefined,
          },
        })),

      // Whether the user has watched the course tour
      watchedCourseTour: false,
      setWatchedCourseTour: (watched: boolean) =>
        set((state) => ({
          ...state,
          watchedCourseTour: watched,
        })),
    }),
    {
      name: "ui",
      partialize: (state) => ({
        watchedCourseTour: state.watchedCourseTour,
      }),
    },
  ),
);

export default useUiStore;
